import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable } from 'rxjs';
import {
  ParkerService,
  LicensePlateService,
  ParkingRightService,
  RegisteredService,
  OrganizationService,
  OrganizationDto,
  RegisteredParkerDto,
  EditParkingRightRequest,
  TimeBasedParkingRightDto,
  ParkingRightTypeEnum
} from '../../../api/gen';
import { toDateInputValue, dateInputValueToIso } from 'shared-lib';
import { describeHttpError } from '../http-error';
import { DateFieldComponent } from '../date-field/date-field.component';

/** What the registry hands the dialog. Everything needed to prefill, so no refetch on open. */
export interface EditSupplierDialogData {
  parkingRightId: string;
  supplierName: string;
  comment: string;
  /**
   * Start of the right. Carried so a save sends it back: the edit replaces the whole time type, and
   * sending null here would silently drop a future start and open the site to the plate today.
   */
  validFrom: string | null;
  validTo: string | null;
  /** Plate numbers currently on the right, paired with their license-plate ids. */
  plates: { id: string; fullNumber: string }[];
  zoneIds: string[];
  availableZones: { id?: string; name?: string | null }[];
  /**
   * The registered parker who owns this right (`purchasedByParker`). Plates must be linked to
   * them as well as to the right — the right governs authorization, the parker link is what the
   * gate resolves a detected plate against. Null for rights with no owning parker, in which case
   * only the right is updated.
   */
  parkerId: string | null;
  /**
   * The company this right was bought for (`purchasedByOrganizationId`). Contact person and phone
   * live on the Organization, not the right, so without this the contact fields cannot be edited.
   * Null for rights registered through the quick single-supplier form, which never sets it.
   */
  organizationId: string | null;
  /**
   * The site this right belongs to. A plate added here is linked to the parker scoped to this site, so
   * it is not also copied onto the parker's rights at other sites.
   */
  parkingSpaceId: string | null;
}

/** A plate row in the editor. `id` is null until a newly typed plate has been registered. */
interface PlateRow {
  id: string | null;
  fullNumber: string;
}

/**
 * Edits an existing long-term (supplier / member) parking right in place.
 *
 * This exists because the registry previously offered only "register" and "revoke", so the only
 * way to add a second plate to a company was to delete it and create it again — which left
 * soft-deleted rights behind and re-bound the plate to an arbitrary parker.
 */
@Component({
  selector: 'app-edit-supplier-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatProgressSpinnerModule, MatDialogModule, TranslatePipe, DateFieldComponent],
  templateUrl: './edit-supplier-dialog.component.html'
})
export class EditSupplierDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<EditSupplierDialogComponent, boolean>);
  protected readonly data = inject<EditSupplierDialogData>(MAT_DIALOG_DATA);
  private readonly parkerService = inject(ParkerService);
  private readonly parkingRightService = inject(ParkingRightService);
  private readonly plateApi = inject(LicensePlateService);
  private readonly registeredService = inject(RegisteredService);
  private readonly orgService = inject(OrganizationService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly t = inject(TranslateService);

  protected readonly name = signal(this.data.supplierName);
  protected readonly comment = signal(this.data.comment);
  // Local date, not the UTC slice — see toDateInputValue. Paired with dateInputValueToIso on save so
  // the round-trip is lossless; the old substring/new Date() pair collapsed a 23:00Z instant to
  // 00:00Z and moved the supplier's expiry a day earlier each time the dialog was saved.
  protected readonly validFrom = signal(toDateInputValue(this.data.validFrom));
  protected readonly validTo = signal(toDateInputValue(this.data.validTo));
  protected readonly plateRows = signal<PlateRow[]>(
    this.data.plates.length
      ? this.data.plates.map(p => ({ id: p.id, fullNumber: p.fullNumber }))
      : [{ id: null, fullNumber: '' }]
  );
  protected readonly selectedZoneIds = signal<string[]>([...this.data.zoneIds]);
  protected readonly isSaving = signal(false);

  // ── The person (RegisteredParker, not the right) ───────────────────────────
  /**
   * Only offered when the right has an owning parker. The right's `name` is the *company*, so
   * without these fields a member's own name could be neither seen nor corrected anywhere in the
   * app — which is why a registered employee appeared nameless on every screen.
   */
  protected readonly canEditParker = !!this.data.parkerId;
  protected readonly parkerFirstName = signal('');
  protected readonly parkerLastName = signal('');
  protected readonly parkerEmail = signal('');
  protected readonly isLoadingParker = signal(false);
  /**
   * As fetched — used to prefill and to detect "nothing changed" on save.
   * A signal, not a field: {@link parkerLoaded} derives from it, and a plain field would only
   * happen to work because it is assigned before the loading flag flips.
   */
  private readonly loadedParker = signal<RegisteredParkerDto | null>(null);

  private async loadParker(): Promise<void> {
    this.isLoadingParker.set(true);
    try {
      const parker = await firstValueFrom(
        this.registeredService.operatorRegisteredParkerRegisteredParkerIdGet(this.data.parkerId!));
      this.loadedParker.set(parker ?? null);
      this.parkerFirstName.set(parker?.firstName ?? '');
      this.parkerLastName.set(parker?.lastName ?? '');
      this.parkerEmail.set(parker?.email ?? '');
    } catch (e) {
      // A no-account parker 404s here (the endpoint is registered-only), and rights bought through the
      // quick supplier form are owned by exactly that. Not an error worth showing: the fields stay
      // hidden and the rest of the dialog works unchanged.
      console.warn('Could not load the owning parker; name fields will not be offered:', e);
      this.loadedParker.set(null);
    } finally {
      this.isLoadingParker.set(false);
    }
  }

  /** True once the parker actually loaded — a no-account-owned right never gets these fields. */
  protected readonly parkerLoaded = computed(() =>
    !this.isLoadingParker() && this.loadedParker() !== null);

  /**
   * Persists the person's name and email.
   *
   * Skipped when nothing changed, so an unrelated edit (a plate, a zone) never rewrites the parker
   * — `EditRegisteredParker` pushes email and name changes on to Keycloak, and a no-op write would
   * touch the identity provider for nothing.
   */
  private async saveParker(): Promise<void> {
    const loaded = this.loadedParker();
    if (!this.canEditParker || !loaded) {
      return;
    }
    const firstName = this.parkerFirstName().trim();
    const lastName = this.parkerLastName().trim();
    const email = this.parkerEmail().trim();

    if (firstName === (loaded.firstName ?? '').trim()
      && lastName === (loaded.lastName ?? '').trim()
      && email === (loaded.email ?? '').trim()) {
      return; // nothing changed
    }

    await firstValueFrom(this.registeredService.operatorRegisteredParkerRegisteredParkerIdPut(
      this.data.parkerId!, { firstName, lastName, email }));
  }

  // ── Company contact (Organization, not the right) ──────────────────────────
  /** Only offered when the right is actually linked to a company. */
  protected readonly canEditContact = !!this.data.organizationId;
  protected readonly contactPerson = signal('');
  protected readonly contactPhone = signal('');
  protected readonly isLoadingContact = signal(false);
  /**
   * The organization as fetched, kept purely to prefill the fields and to detect "nothing changed"
   * on save. Nothing from it is written back — the contact endpoint takes only the two fields.
   */
  private loadedOrg: OrganizationDto | null = null;

  constructor() {
    if (this.canEditContact) {
      void this.loadContact();
    }
    if (this.canEditParker) {
      void this.loadParker();
    }
  }

  private async loadContact(): Promise<void> {
    this.isLoadingContact.set(true);
    try {
      // The body is required, not optional: the endpoint reads its flags directly, so omitting it
      // threw a NullReferenceException server-side and the edit dialog failed to open at all.
      // Only the contact fields are needed here, so every include stays off.
      const org = await firstValueFrom(
        this.orgService.organizationsOrganizationIdFetchPost(this.data.organizationId!, {
          includeDeleted: false,
          includeValidity: false,
          includeMembers: false
        }));
      this.loadedOrg = org ?? null;
      this.contactPerson.set(org?.contactPerson ?? '');
      this.contactPhone.set(org?.contactPhone ?? '');
    } catch (e) {
      // A failed contact load must not block editing plates — the fields simply stay empty and
      // are not sent on save, so nothing is overwritten with a blank.
      console.error('Could not load company contact details:', e);
      this.loadedOrg = null;
    } finally {
      this.isLoadingContact.set(false);
    }
  }

  /**
   * Persists contact person/phone onto the Organization. Skipped entirely when the org could not
   * be loaded, so a failed fetch never blanks a stored contact.
   *
   * Uses the contact-only endpoint rather than the full organization PUT on purpose: that one
   * requires billing address and configuration, so saving a phone number would mean reading
   * billing and writing it straight back — overwriting anything another operator changed in the
   * meantime, and re-creating the billing entities on every save.
   */
  private async saveContact(): Promise<void> {
    if (!this.canEditContact || !this.loadedOrg) {
      return;
    }
    const person = this.contactPerson().trim();
    const phone = this.contactPhone().trim();
    if (person === (this.loadedOrg.contactPerson ?? '') && phone === (this.loadedOrg.contactPhone ?? '')) {
      return; // nothing changed
    }

    // PUT organizations/{id}/contact is newly added; the cast keeps this compiling against the
    // not-yet-regenerated API client. `npm run generate:api:operator` makes it a no-op.
    const api = this.orgService as OrganizationService & {
      organizationsOrganizationIdContactPut(
        organizationId: string,
        body: { contactPerson?: string; contactPhone?: string }
      ): Observable<OrganizationDto>;
    };
    await firstValueFrom(api.organizationsOrganizationIdContactPut(
      this.data.organizationId!, { contactPerson: person, contactPhone: phone }));
  }

  protected readonly canSave = computed(() =>
    this.name().trim().length > 0 && this.plateRows().some(r => r.fullNumber.trim().length > 0));

  protected addPlate(): void {
    this.plateRows.update(rows => [...rows, { id: null, fullNumber: '' }]);
  }

  protected removePlate(index: number): void {
    this.plateRows.update(rows => rows.length > 1 ? rows.filter((_, i) => i !== index) : rows);
  }

  /**
   * Editing a plate's text detaches it from its previously resolved id — the row now describes a
   * different vehicle and must be re-registered on save rather than silently keeping the old link.
   */
  protected onPlateChange(index: number, value: string): void {
    this.plateRows.update(rows => rows.map((row, i) =>
      i === index ? { id: row.fullNumber === value ? row.id : null, fullNumber: value } : row));
  }

  protected isZoneSelected(zoneId: string | undefined): boolean {
    return !!zoneId && this.selectedZoneIds().includes(zoneId);
  }

  protected toggleZone(zoneId: string | undefined): void {
    if (!zoneId) return;
    this.selectedZoneIds.update(ids =>
      ids.includes(zoneId) ? ids.filter(id => id !== zoneId) : [...ids, zoneId]);
  }

  protected cancel(): void {
    this.dialogRef.close(false);
  }

  protected async save(): Promise<void> {
    if (this.isSaving() || !this.canSave()) {
      return;
    }
    // ISO dates compare correctly as strings. The backend refuses from >= to; say it before writing.
    if (this.validFrom() && this.validTo() && this.validFrom() >= this.validTo()) {
      this.snackBar.open(this.t.instant('hotline.longTerm.err.range'), 'Close', { duration: 4000 });
      return;
    }
    this.isSaving.set(true);
    try {
      const licensePlateIds = await this.resolvePlateIds();
      if (!licensePlateIds.length) {
        this.snackBar.open(this.t.instant('hotline.msg.enterPlate'), 'Close', { duration: 3000 });
        return;
      }

      const conflict = await this.findPlateOwnedByOther(licensePlateIds);
      if (conflict) {
        this.snackBar.open(this.t.instant('hotline.msg.plateOwnedByOther', conflict), 'Close', { duration: 6000 });
        return;
      }

      const zoneIds = this.selectedZoneIds();
      const typeDto: TimeBasedParkingRightDto = {
        type: ParkingRightTypeEnum.TimeBased,
        validFrom: dateInputValueToIso(this.validFrom()),
        validTo: dateInputValueToIso(this.validTo()),
        startTime: null,
        endTime: null,
        validDays: null,
        isAllDay: true,
        isRecurring: false
      };

      // Every collection is sent explicitly: EditParkingRight treats a non-null list as a
      // replacement, so omitting one would silently keep stale plates or zones.
      const request: EditParkingRightRequest = {
        typeDto,
        parkingTypeId: null,
        allowedParkingSpaces: null,
        allowedParkingZones: zoneIds.length ? zoneIds : [],
        allowedLicensePlates: licensePlateIds,
        purchasedByParker: null,
        purchasedByOrganizationId: null,
        paymentReferenceId: null,
        name: this.name().trim(),
        comment: this.comment().trim()
      };

      await firstValueFrom(
        this.parkingRightService.parkingRightsParkingRightIdPut(this.data.parkingRightId, request));

      // The right and the parker are two separate links. Updating only the right leaves the new
      // plate unattached to the member, so the gate never resolves it back to them.
      await this.syncParkerPlates(licensePlateIds);

      // The person's name lives on the parker, not the right. Saved after the right so a failure
      // here cannot undo an otherwise-successful plate change.
      await this.saveParker();

      // Contact details live on the Organization. Saved after the right so a contact-only failure
      // cannot roll back an otherwise-successful plate change.
      await this.saveContact();

      this.snackBar.open(this.t.instant('hotline.msg.updated'), 'Close', { duration: 3000 });
      this.dialogRef.close(true);
    } catch (e) {
      console.error('Error updating parking right:', e);
      // Show the server's reason when it sent one. The most likely failure here is an email that
      // already belongs to another parker, and "Update failed" gives the operator no way to know
      // that — let alone that the fix is to assign the plate to the existing parker instead.
      const reason = describeHttpError(e);
      this.snackBar.open(
        reason
          ? `${this.t.instant('hotline.msg.updateFailed')} ${reason}`
          : this.t.instant('hotline.msg.updateFailed'),
        'Close',
        { duration: 6000 });
    } finally {
      this.isSaving.set(false);
    }
  }

  /**
   * Finds an added plate that another registered parker currently holds.
   *
   * Checked before anything is saved. Linking such a plate to this member never detaches the other
   * holder, so the plate ends up with two owners; and when the link is refused (e.g. a car is
   * parked) the right keeps the plate anyway. Either way the plate page and the registry then
   * name different people for the same plate. Guest holders are not a conflict: registering a new
   * plate attaches one by design. Only added plates are checked, one request each.
   */
  private async findPlateOwnedByOther(licensePlateIds: string[]): Promise<{ plate: string; owner: string } | null> {
    const parkerId = this.data.parkerId;
    if (!parkerId) {
      return null;
    }

    const before = new Set(this.data.plates.map(p => p.id));
    for (const licensePlateId of licensePlateIds.filter(id => !before.has(id))) {
      const ownership = await firstValueFrom(
        this.plateApi.operatorLicensePlateLicensePlateIdOwnershipGet(licensePlateId));
      const other = (ownership?.holders ?? []).find(h => h.isRegistered && h.parkerId !== parkerId);
      if (other) {
        const owner = [other.firstName, other.lastName].filter(Boolean).join(' ') || other.email || other.parkerId || '';
        return { plate: ownership?.fullNumber ?? '', owner };
      }
    }
    return null;
  }

  /**
   * Brings the owning parker's plate links in line with the right's plates.
   *
   * Mirrors what the registration flow does per member: `parkerRegisterLicensePlatePost` only
   * creates the plate (attaching it to a fresh no-account parker), so the member link has to be made
   * explicitly. Removals are unlinked too, otherwise a plate taken off the right keeps resolving
   * to this member at the gate and pollutes the plate→parker lookup.
   *
   * Failures here are reported but do not fail the save: the right itself is already updated,
   * and re-opening the dialog and saving again re-attempts the links.
   */
  private async syncParkerPlates(licensePlateIds: string[]): Promise<void> {
    const parkerId = this.data.parkerId;
    if (!parkerId) {
      return;
    }

    const before = new Set(this.data.plates.map(p => p.id));
    const after = new Set(licensePlateIds);
    const added = licensePlateIds.filter(id => !before.has(id));
    const removed = [...before].filter(id => !after.has(id));

    try {
      for (const licensePlateId of added) {
        await firstValueFrom(
          this.registeredService.operatorRegisteredParkerRegisteredParkerIdAssignLicensePlateLicensePlateIdPost(
            parkerId, licensePlateId, this.data.parkingSpaceId ?? undefined));
      }
      for (const licensePlateId of removed) {
        await firstValueFrom(
          this.registeredService.operatorRegisteredParkerRegisteredParkerIdRemoveLicensePlateLicensePlateIdPost(
            parkerId, licensePlateId, this.data.parkingSpaceId ?? undefined));
      }
    } catch (e) {
      console.error('Parking right saved, but syncing the parker\'s license plates failed:', e);
      this.snackBar.open(this.t.instant('hotline.msg.parkerPlateSyncFailed'), 'Close', { duration: 5000 });
    }
  }

  /**
   * Turns the plate rows into license-plate ids, registering any that are new.
   * Normalisation matches the gate's separator-free ANPR reading, so "LL 123 AB" and "LL-123AB"
   * resolve to the same plate instead of creating a second one.
   */
  private async resolvePlateIds(): Promise<string[]> {
    const ids: string[] = [];
    for (const row of this.plateRows()) {
      const fullNumber = row.fullNumber.trim().toUpperCase().replace(/[\s\-_]/g, '');
      if (!fullNumber) {
        continue;
      }
      if (row.id) {
        ids.push(row.id);
        continue;
      }
      const plate = await firstValueFrom(
        this.parkerService.parkerRegisterLicensePlatePost({ fullNumber }));
      if (plate?.id) {
        ids.push(plate.id);
      }
    }
    // A plate listed twice would be sent twice and duplicate the join row.
    return [...new Set(ids)];
  }
}
